/**
 * PaymentForm Component
 * Stripe Elements integration for processing deposit payments
 */

import React, { useState, FormEvent } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import type { PaymentFormProps } from '@/types';
import { stripePromise } from '@/lib/stripe';

// Inner form component that uses Stripe hooks
const PaymentFormInner: React.FC<PaymentFormProps> = ({
  clientSecret,
  depositAmount,
  onSuccess,
  onError
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setProcessing(false);
      return;
    }

    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            card: cardElement as any, // Type assertion needed for Stripe Elements compatibility
          }
        }
      );

      if (confirmError) {
        const errorMessage = confirmError.message || 'Payment failed';
        setError(errorMessage);
        onError(errorMessage);
        setProcessing(false);
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      onError(message);
      setProcessing(false);
    }
  };

  const formatAmount = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Card Input Section */}
      <div>
        <label htmlFor="card-element" className="block text-sm font-medium text-neutral-700 mb-2">
          Card Details
        </label>
        <div className="border border-neutral-300 rounded-lg p-3 bg-white focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-colors">
          <CardElement
            id="card-element"
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1f2937',
                  '::placeholder': { color: '#9ca3af' }
                },
                invalid: {
                  color: '#ef4444'
                }
              },
              hidePostalCode: false
            }}
            onChange={(e: StripeCardElementChangeEvent) => setCardComplete(e.complete)}
          />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Security Message */}
      <div className="flex items-center justify-center text-xs text-neutral-500 py-2">
        <svg
          className="w-4 h-4 mr-1.5 text-neutral-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span>Secured by Stripe</span>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || processing || !cardComplete}
        className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-all flex items-center justify-center ${
          !stripe || processing || !cardComplete
            ? 'bg-neutral-300 cursor-not-allowed'
            : 'bg-accent-600 hover:bg-accent-700 focus:ring-2 focus:ring-accent-500 focus:ring-offset-2'
        }`}
      >
        {processing ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Processing Payment...
          </>
        ) : (
          <>
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
            Pay {formatAmount(depositAmount)}
          </>
        )}
      </button>
    </form>
  );
};

// Outer component that wraps with Stripe Elements provider
const PaymentForm: React.FC<PaymentFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentFormInner {...props} />
    </Elements>
  );
};

export default PaymentForm;
